import { useNavigate } from 'react-router-dom';
import { StyledCell } from './styles';
import { Box, Chip } from '@mui/material';
import { useAuthStore } from '../../../../Stores/useAuthStore';
import React, { useCallback } from 'react';
import { ChipStatus } from '../../../../Components/ChipStatus.tsx/ChipStatus';
import { format } from 'date-fns';
import { DashboardScrollTable } from '../../../../Components/DashboardTable/DashboardScrollTable';
import { JOBS } from '../../../../Routing/paths';
import { useScrollStore } from '../../../../Stores/useGeneralAppStore';

const headCells: HeadCell[] = [
  {
    id: 'jobReference',
    numeric: true,
    disablePadding: false,
    label: 'Job reference',
  },
  {
    id: 'position',
    numeric: true,
    disablePadding: false,
    label: 'Position name',
  },
  {
    id: 'client',
    numeric: false,
    disablePadding: false,
    label: 'Client',
  },
  {
    id: 'departmentId',
    numeric: false,
    disablePadding: false,
    label: 'Department',
  },
  {
    id: 'headcount',
    numeric: true,
    disablePadding: false,
    label: 'Headcount',
  },
  {
    id: 'skills',
    numeric: true,
    disablePadding: false,
    label: 'Skills',
  },
  {
    id: 'status',
    numeric: true,
    disablePadding: false,
    label: 'Job status',
  },
  {
    id: 'creationDateTime',
    disablePadding: false,
    label: 'Creation Date',
  },
];

interface JobsTableProps {
  rows: any[];
  loadMore: () => void;
  loading: boolean;
  hasMore: boolean;
  totalItems: number;
}

export const JobsTable = ({ rows, loadMore, loading, hasMore, totalItems }: JobsTableProps) => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { setSelectedSourcePage, setSelectedScrollPosition } = useScrollStore();

  const rowCanBeClicked = useCallback(() => {
    return user?.isAdmin || user?.isRecruiter;
  }, [user]);

  const handleLoadMore = useCallback(() => {
    if (hasMore && !loading) {
      loadMore();
    }
  }, [hasMore, loading, loadMore]);

  const handleRowClick = (index: number, link: string) => {
    console.log('index', index);
    console.log('link', link);
    setSelectedSourcePage(JOBS);
    setSelectedScrollPosition(index);
    navigate(link);
  };

  const rowContent = (index: number, row: any) => {
    const jobLink = `${JOBS}/${row.id}`;

    return (
      <React.Fragment>
        <StyledCell
          key="ref"
          canClick={rowCanBeClicked()}
          onClick={rowCanBeClicked() ? () => handleRowClick(index, jobLink) : undefined}
          sx={{ width: '8rem' }}
        >
          {row.jobReference}
        </StyledCell>
        <StyledCell
          key="position"
          canClick={rowCanBeClicked()}
          onClick={rowCanBeClicked() ? () => handleRowClick(index, jobLink) : undefined}
        >
          {row.position}
        </StyledCell>
        <StyledCell
          key="client"
          canClick={rowCanBeClicked()}
          onClick={rowCanBeClicked() ? () => handleRowClick(index, jobLink) : undefined}
        >
          {row?.client?.name}
        </StyledCell>
        <StyledCell
          key="dept"
          canClick={rowCanBeClicked()}
          onClick={rowCanBeClicked() ? () => handleRowClick(index, jobLink) : undefined}
        >
          {row?.jobDepartment?.name}
        </StyledCell>
        <StyledCell
          key="headcount"
          canClick={rowCanBeClicked()}
          onClick={rowCanBeClicked() ? () => handleRowClick(index, jobLink) : undefined}
        >
          {row.headcount}
        </StyledCell>
        <StyledCell
          key="skills"
          canClick={rowCanBeClicked()}
          onClick={rowCanBeClicked() ? () => handleRowClick(index, jobLink) : undefined}
        >
          <Box display="flex" alignItems="center">
            {row.skills?.length > 0 ? (
              <>
                {row.skills.slice(0, 2).map((skill: string) => (
                  <Chip label={skill} sx={{ marginRight: 1 }} key={skill} />
                ))}
                {row.skills.length > 2 && (
                  <Chip label={`+${row.skills.length - 2}`} sx={{ marginRight: 1 }} />
                )}
              </>
            ) : (
              '-'
            )}
          </Box>
        </StyledCell>
        <StyledCell
          key="status"
          canClick={rowCanBeClicked()}
          onClick={rowCanBeClicked() ? () => handleRowClick(index, jobLink) : undefined}
        >
          <ChipStatus status={row.status} />
        </StyledCell>
        <StyledCell
          key="date"
          canClick={rowCanBeClicked()}
          onClick={rowCanBeClicked() ? () => handleRowClick(index, jobLink) : undefined}
        >
          {format(new Date(row.creationDateTime), 'dd-MM-yyyy')}
        </StyledCell>
      </React.Fragment>
    );
  };

  return (
    <DashboardScrollTable
      headerCells={headCells}
      rows={rows}
      loadMore={handleLoadMore}
      hasMore={hasMore}
      rowContent={rowContent}
      totalItems={totalItems}
      pageNavigation={JOBS}
    />
  );
};
